// src/pages/contracts/ContractDetailsPage.js - UPDATED WITH CLIENT FETCH
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  Upload,
  Send,
  FileText,
  Edit,
  Trash2,
  Copy,
  Calendar,
  User,
  Building,
  DollarSign,
  Clock,
  AlertCircle,
  CheckCircle,
  Eye,
  Mail,
  Phone
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import ContractStatusFlow from './components/ContractStatusFlow';
import ContractPDFViewer from './components/ContractPDFViewer';
import { contractService, clientService, serviceService, templateService } from '../../services';
import { formatDate, formatCurrency } from '../../utils/formatters';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import TerminationNoticeActions from './components/TerminationNoticeActions';
import AdditionalActActions from './components/AdditionalActActions';
import AccessCodeManagement from '../../components/contracts/AccessCodeManagement';
import AdminUploadedDocumentsViewer from '../../components/contracts/AdminUploadedDocumentsViewer';
import { ContractForm } from '../../components/forms';

const ContractDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showContractForm, setShowContractForm] = useState(false);

  // Fetch contract details
  const { data: contract, isLoading, error } = useQuery(
    ['contract', id],
    () => contractService.getById(id),
    {
      enabled: !!id,
      retry: 1
    }
  );

  // Fetch client details based on contract.clientId
  const { data: client, isLoading: isClientLoading } = useQuery(
    ['client', contract?.clientId],
    () => clientService.getById(contract.clientId),
    {
      enabled: !!contract?.clientId,
      retry: 1,
      onError: (error) => {
        console.error('Failed to load client details:', error);
      }
    }
  );

  // Fetch clients for contract form
  const { data: clients = [] } = useQuery('clients', clientService.getAll, {
    onError: (error) => {
      console.error('Failed to load clients:', error);
    }
  });

  // Fetch services for contract form
  const { data: services = [] } = useQuery('services', serviceService.getAll, {
    onError: (error) => {
      console.error('Failed to load services:', error);
    }
  });

  // Fetch templates for contract form
  const { data: templates = [] } = useQuery('templates', templateService.getActive, {
    onError: (error) => {
      console.error('Failed to load templates:', error);
    }
  });

  console.log('Contract:', contract);
  console.log('Client:', client);

  // Mutations
  const generatePdfMutation = useMutation(contractService.generatePdf, {
    onSuccess: () => {
      queryClient.invalidateQueries(['contract', id]);
      toast.success('PDF generat cu succes');
    },
    onError: (error) => {
      console.error('PDF generation error:', error);
      toast.error('Eroare la generarea PDF: ' + (error.response?.data?.message || error.message));
    }
  });

  const sendAccessLinkMutation = useMutation(contractService.getAccessLink, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(['contract', id]);
      toast.success('Link de acces generat cu succes');
      // Optionally copy link to clipboard
      if (data.accessLink) {
        navigator.clipboard.writeText(window.location.origin + data.accessLink);
        toast.success('Link copiat în clipboard');
      }
    },
    onError: (error) => {
      console.error('Access link error:', error);
      toast.error('Eroare la generarea link-ului: ' + (error.response?.data?.message || error.message));
    }
  });

  const uploadSignedMutation = useMutation(
    ({ id, file }) => contractService.uploadSignedDocument(id, file),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['contract', id]);
        toast.success('Document semnat încărcat cu succes');
      },
      onError: (error) => {
        console.error('Upload error:', error);
        toast.error('Eroare la încărcarea documentului: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  const updateContractMutation = useMutation(
    (contractData) => contractService.update(id, contractData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['contract', id]);
        queryClient.invalidateQueries('contracts');
        toast.success('Contract actualizat cu succes');
        setShowContractForm(false);
      },
      onError: (error) => {
        console.error('Update error:', error);
        toast.error('Eroare la actualizarea contractului: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  const deleteContractMutation = useMutation(contractService.delete, {
    onSuccess: () => {
      toast.success('Contract șters cu succes');
      navigate('/contracts');
    },
    onError: (error) => {
      console.error('Delete error:', error);
      toast.error('Eroare la ștergerea contractului: ' + (error.response?.data?.message || error.message));
    }
  });

  const handleGeneratePdf = () => {
    generatePdfMutation.mutate(id);
  };

  const handleSendAccessLink = () => {
    sendAccessLinkMutation.mutate(id);
  };

  const handleUploadSigned = (file) => {
    uploadSignedMutation.mutate({ id, file });
  };

  const handleContractSubmit = async (contractData) => {
    try {
      await updateContractMutation.mutateAsync(contractData);
    } catch (error) {
      // Error handled in mutation callbacks
    }
  };

  const handleDelete = () => {
    if (window.confirm('Sigur vrei să ștergi acest contract? Această acțiune nu poate fi anulată.')) {
      deleteContractMutation.mutate(id);
    }
  };

  const handleDownload = async () => {
    try {
      if (contract?.pdfFilePath) {
        // Use the downloadPdf service method
        const { blob, filename } = await contractService.downloadPdf(id);

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || `contract_${id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success('Contract descărcat cu succes');
      } else {
        toast.error('PDF-ul nu a fost generat încă');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Eroare la descărcarea contractului: ' + (error.response?.data?.message || error.message));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiat în clipboard');
  };

  const handleActChange = () => {
    // Invalidate the 'contract' query to force a re-fetch of the latest data
    queryClient.invalidateQueries(['contract', id]);
    toast.success('Lista de acte adiționale a fost actualizată.');
  };

  // NOU: Funcție de refresh pentru Notificările de Reziliere
  const handleNoticeChange = () => {
    queryClient.invalidateQueries(['contract', id]);
    toast.success('Lista de notificări de reziliere a fost actualizată.');
  };

  if (isLoading || isClientLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Eroare la încărcarea contractului
          </h2>
          <p className="text-gray-600 mb-4">{error.response?.data?.message || error.message}</p>
          <button
            onClick={() => navigate('/contracts')}
            className="btn btn-primary"
          >
            Înapoi la contracte
          </button>
        </div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Contract nu a fost găsit
          </h2>
          <button
            onClick={() => navigate('/contracts')}
            className="btn btn-primary"
          >
            Înapoi la contracte
          </button>
        </div>
      </div>
    );
  }

  const getTotalValue = () => {
    return contract.services?.reduce((total, service) => {
      return total + (service.quantity * service.price);
    }, 0) || 0;
  };

  const isExpiringSoon = contract.daysUntilExpiry <= 30 && contract.daysUntilExpiry > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/contracts')}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-5 h-5 mr-1" />
                Înapoi
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Contract {contract.number}
                </h1>
                <p className="text-gray-600">{contract.clientName}</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {/* Buton pentru generare PDF când nu există PDF */}
              {!contract.pdfFilePath && (
                <button
                  onClick={handleGeneratePdf}
                  disabled={generatePdfMutation.isLoading}
                  className="btn btn-primary"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  {generatePdfMutation.isLoading ? 'Se generează...' : 'Generează PDF'}
                </button>
              )}

              {/* Buton pentru regenerare PDF când există deja PDF */}
              {contract.pdfFilePath && (
                <button
                  onClick={handleGeneratePdf}
                  disabled={generatePdfMutation.isLoading}
                  className="btn btn-secondary"
                  title="Regenerează PDF-ul contractului"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  {generatePdfMutation.isLoading ? 'Se regenerează...' : 'Regenerează PDF'}
                </button>
              )}

              {contract.status === 'Generated' && (
                <button
                  onClick={handleSendAccessLink}
                  disabled={sendAccessLinkMutation.isLoading}
                  className="btn btn-primary"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sendAccessLinkMutation.isLoading ? 'Se generează...' : 'Generează Link'}
                </button>
              )}

              {contract.pdfFilePath && (
                <button
                  onClick={handleDownload}
                  className="btn btn-outline"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descarcă
                </button>
              )}

              {contract.pdfFilePath && (
                <button
                  onClick={() => setShowPDFViewer(true)}
                  className="btn btn-outline"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Vizualizare
                </button>
              )}

              <button
                onClick={() => setShowContractForm(true)}
                className="btn btn-outline"
              >
                <Edit className="w-4 h-4 mr-2" />
                Editează
              </button>

              <button
                onClick={handleDelete}
                disabled={deleteContractMutation.isLoading}
                className="btn btn-outline text-red-600 border-red-300 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Șterge
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Status Flow */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Status Contract
              </h2>
              <ContractStatusFlow contract={contract} />
            </div>

            <AccessCodeManagement
              contractId={contract.id}
              client={client}
              onAccessGenerated={(data) => {
                console.log('Access generated:', data);
                // Refresh contract data
              }}
            />

            {/* Alert for expiring contracts */}
            {isExpiringSoon && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 text-orange-600 mr-3" />
                  <div>
                    <h3 className="text-sm font-medium text-orange-800">
                      Contract în curs de expirare
                    </h3>
                    <p className="text-sm text-orange-700 mt-1">
                      Acest contract expiră în {contract.daysUntilExpiry} zile.
                      Consideră programarea unei reînnoiri.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Signed Documents Section - Always visible */}
            <div className="mt-8">
              <AdminUploadedDocumentsViewer
                contractId={contract.id}
                contractNumber={contract.number}
                additionalActs={contract.additionalActs || []}
                notices={contract.notices || []}
              />
            </div>

            {/* Services */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Servicii Contractate
              </h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Serviciu
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        U.M.
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Cantitate
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Preț Unitar
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {contract.services?.map((service, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {service.serviceName}
                            </div>
                            {service.serviceDescription && (
                              <div className="text-sm text-gray-500">
                                {service.serviceDescription}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {service.unit}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {service.quantity}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(service.price)} {service.currency}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatCurrency(service.totalPrice)} {service.currency}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan="4" className="px-6 py-3 text-right text-sm font-medium text-gray-900">
                        Total Contract:
                      </td>
                      <td className="px-6 py-3 text-sm font-bold text-gray-900">
                        {formatCurrency(getTotalValue())} RON
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex flex-col">
              <div className="section">
                <h2>Acte Adiționale</h2>
                <AdditionalActActions
                  contractId={contract.id}
                  contractNumber={contract.number}
                  additionalActs={contract.additionalActs || []}
                  formatDate={formatDate}
                  onActChange={handleActChange}
                  client={client} // Pass the fetched client data
                  availableServices={services}
                  contractServices={contract.services || []}
                />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex flex-col">
              <div className="section">
                <h2>Notificare de Reziliere</h2>
                <TerminationNoticeActions
                  contractId={contract.id}
                  contractNumber={contract.number}
                  terminationNotices={contract.notices || []}
                  formatDate={formatDate}
                  onNoticeChange={handleNoticeChange}
                  client={client} // Pass the fetched client data
                />
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contract Info */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Informații Contract
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Număr:</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-900">
                      {contract.number}
                    </span>
                    <button
                      onClick={() => copyToClipboard(contract.number)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Status:</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${contract.status === 'Signed' ? 'bg-green-100 text-green-800' :
                    contract.status === 'Expired' ? 'bg-red-100 text-red-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                    {contract.statusText}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Data început:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatDate(contract.startDate)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Data expirare:</span>
                  <span className={`text-sm font-medium ${isExpiringSoon ? 'text-orange-600' : 'text-gray-900'}`}>
                    {formatDate(contract.dueDate)}
                  </span>
                </div>

                {contract.templateName && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Template:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {contract.templateName}
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Accesat:</span>
                  <span className={`text-sm font-medium ${contract.hasBeenAccessed ? 'text-green-600' : 'text-gray-400'}`}>
                    {contract.hasBeenAccessed ? 'Da' : 'Nu'}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Creat:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatDate(contract.createdAt)}
                  </span>
                </div>
              </div>
            </div>

            {/* Client Info */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Informații Client
              </h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <Building className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {contract.clientName}
                    </div>
                    <div className="text-sm text-gray-600">
                      Client
                    </div>
                  </div>
                </div>

                {/* Display client emails if available */}
                {client && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    {client.email && (
                      <div className="flex items-center space-x-2 text-sm text-gray-600 mb-2">
                        <Mail className="w-4 h-4" />
                        <span>{client.email}</span>
                      </div>
                    )}
                    {client.representativeEmail && (
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Mail className="w-4 h-4" />
                        <span>{client.representativeEmail}</span>
                      </div>
                    )}
                  </div>
                )}

                <button
                  onClick={() => navigate(`/clients/${contract.clientId}`)}
                  className="w-full text-left text-sm text-primary-600 hover:text-primary-700"
                >
                  Vezi detalii client →
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* PDF Viewer Modal */}
      {showPDFViewer && contract.pdfFilePath && (
        <div className="fixed inset-0 z-50">
          <ContractPDFViewer
            contract={contract}
            pdfUrl={`/contracts/${id}/download-pdf`}
            onClose={() => setShowPDFViewer(false)}
            onDownload={handleDownload}
            onUploadSigned={handleUploadSigned}
            isFullscreen={isFullscreen}
            onToggleFullscreen={() => setIsFullscreen(!isFullscreen)}
            isPublic={false}
          />
        </div>
      )}

      {/* Contract Form Modal */}
      <ContractForm
        contract={contract}
        clients={clients}
        services={services}
        templates={templates}
        isOpen={showContractForm}
        onClose={() => setShowContractForm(false)}
        onSubmit={handleContractSubmit}
        loading={updateContractMutation.isLoading}
      />
    </div>
  );
};

export default ContractDetailsPage;